/**
 * The five rolling tenors and their fixed colors.
 *
 * Color follows the TENOR, never the row order: "36-month is the gold line"
 * must stay true whatever else the chart contains. These are the brand-locked
 * `--market-term-*` tokens (see the long note in `index.css`) — do not
 * substitute the app-wide `--chart-*` ramp, which shifts per palette.
 *
 * There is no sixth color, so a sixth tenor is dropped rather than invented.
 */

import type { PeakBlock, StripTenor } from "@/types/market-intelligence";

export const STRIP_TENORS: readonly StripTenor[] = [12, 24, 36, 48, 60] as const;

export const TENOR_COLOR_VAR: Record<StripTenor, string> = {
  12: "var(--market-term-12)",
  24: "var(--market-term-24)",
  36: "var(--market-term-36)",
  48: "var(--market-term-48)",
  60: "var(--market-term-60)",
};

/**
 * The peak block's stroke pattern — the chart's second visual channel.
 *
 * Colour is locked to the tenor (see above), so when a view crosses tenors
 * with blocks the block has to be carried by something else. ATC stays solid
 * because it is the default and the overwhelmingly common selection: an
 * untouched chart must look exactly as it did before blocks became
 * multi-select.
 */
export const PEAK_LINE_STYLE: Record<
  PeakBlock,
  "solid" | "dashed" | "dotted"
> = {
  atc: "solid",
  on_peak: "dashed",
  off_peak: "dotted",
};

/**
 * The block selection for gas, which is quoted around-the-clock only.
 *
 * A module-level constant rather than an inline `["atc"]` so the identity is
 * stable across renders: the views feed it straight into a `useMemo`
 * dependency list, and a fresh array each render defeats the memo.
 */
export const GAS_PEAKS: readonly PeakBlock[] = ["atc"];

export const DAY_AHEAD_COLOR_VAR = "var(--market-day-ahead)";
export const REAL_TIME_COLOR_VAR = "var(--market-real-time)";

export function tenorLabel(tenor: StripTenor): string {
  return `${tenor} mo`;
}
